import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { ChevronDown, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Artist } from 'types';

interface ProfileSelectorProps {
  profiles: Artist[];
  selectedProfile: Artist | null;
  onProfileSelect: (profile: Artist) => void;
  className?: string;
}

export function ProfileSelector({
  profiles,
  selectedProfile,
  onProfileSelect,
  className = '',
}: ProfileSelectorProps) {
  const { t } = useTranslation('collab-club');
  const [isOpen, setIsOpen] = useState(false);

  if (profiles.length === 0) {
    return <div className={`text-gray-500 text-sm ${className}`}>{t('no_profiles')}</div>;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-gray-600">{t('sending_as')}:</span>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-auto p-2 justify-between min-w-[200px]">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage
                  src={selectedProfile?.image || '/placeholder.svg'}
                  alt={selectedProfile?.name || ''}
                />
                <AvatarFallback className="text-xs">
                  {selectedProfile?.name ? (
                    selectedProfile.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                  ) : (
                    <User className="h-3 w-3" />
                  )}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-sm">
                {selectedProfile?.name || t('select_profile')}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-[200px]">
          {profiles.map((profile) => (
            <DropdownMenuItem
              key={profile.id}
              onClick={() => {
                onProfileSelect(profile);
                setIsOpen(false);
              }}
              className="flex items-center gap-2 p-2"
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={profile.image || '/placeholder.svg'} alt={profile.name} />
                <AvatarFallback className="text-xs">
                  {profile.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium">{profile.name}</div>
                <div className="truncate text-xs text-gray-500">
                  {profile.genres?.join(', ') || 'Unknown'}
                </div>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
